import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { ScrollToTop } from "./components/ScrollToTop";
import { Home } from "./pages/Home";
import { Booking } from "./pages/Booking";
import { BookingSuccess } from "./pages/BookingSuccess";
import { Equipment } from "./pages/Equipment";
import { EquipmentDetail } from "./pages/EquipmentDetail";
import { EquipmentRentalSuccess } from "./pages/EquipmentRentalSuccess";
import { Gallery } from "./pages/Gallery";
import { Contact } from "./pages/Contact";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { Terms } from "./pages/Terms";
import { Directions } from "./pages/Directions";
import { ClosedNotice } from "./pages/ClosedNotice";
import { AdminAuthProvider } from "./contexts/AdminAuthContext";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminRentals from "./pages/admin/AdminRentals";
import AdminEquipment from "./pages/admin/AdminEquipment";
import AdminRvSites from "./pages/admin/AdminRvSites";
import AdminGallery from "./pages/admin/AdminGallery";
import AdminContacts from "./pages/admin/AdminContacts";
import AdminSubscribers from "./pages/admin/AdminSubscribers";
import AdminSettings from "./pages/admin/AdminSettings";
import { Toaster } from "./components/ui/sonner";

// =========================================================================
// SITE CLOSURE FLAG
// Flip to `false` (then redeploy) to reopen the public site.
// While true: every public route ("/", "/booking", "/equipment", ...) renders
// a full-screen closure page with no header/footer/nav. Admin routes remain
// fully functional so the owner can manage the site behind the scenes.
// =========================================================================
const SITE_CLOSED = false;

const PublicChrome = ({ children }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  // Hide header/footer on admin routes (they have their own chrome) AND while
  // the site is closed — the closure page must be visually chromeless.
  const chromeless = isAdmin || SITE_CLOSED;
  return (
    <>
      {!chromeless && <Header />}
      {children}
      {!chromeless && <Footer />}
    </>
  );
};

const PublicRoutes = () => (
  <>
    <Route path="/" element={<Home />} />
    <Route path="/booking" element={<Booking />} />
    <Route path="/booking/success" element={<BookingSuccess />} />
    <Route path="/equipment" element={<Equipment />} />
    <Route path="/equipment/:id" element={<EquipmentDetail />} />
    <Route path="/equipment/rental/success" element={<EquipmentRentalSuccess />} />
    <Route path="/gallery" element={<Gallery />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/terms" element={<Terms />} />
    <Route path="/directions" element={<Directions />} />
  </>
);

const AdminRoutes = () => (
  <>
    <Route path="/admin/login" element={<AdminLogin />} />
    <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
    <Route path="/admin/bookings" element={<AdminLayout><AdminBookings /></AdminLayout>} />
    <Route path="/admin/rentals" element={<AdminLayout><AdminRentals /></AdminLayout>} />
    <Route path="/admin/equipment" element={<AdminLayout><AdminEquipment /></AdminLayout>} />
    <Route path="/admin/rv-sites" element={<AdminLayout><AdminRvSites /></AdminLayout>} />
    <Route path="/admin/gallery" element={<AdminLayout><AdminGallery /></AdminLayout>} />
    <Route path="/admin/contacts" element={<AdminLayout><AdminContacts /></AdminLayout>} />
    <Route path="/admin/subscribers" element={<AdminLayout><AdminSubscribers /></AdminLayout>} />
    <Route path="/admin/settings" element={<AdminLayout><AdminSettings /></AdminLayout>} />
  </>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AdminAuthProvider>
          <PublicChrome>
            <Routes>
              {AdminRoutes()}
              {SITE_CLOSED ? (
                // Catch-all for every non-admin route → closure page
                <Route path="*" element={<ClosedNotice />} />
              ) : (
                PublicRoutes()
              )}
            </Routes>
          </PublicChrome>
          <Toaster />
        </AdminAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
